import React from "react";
import { Attention, Button, DialogNew, Typography } from "@optimizely/axiom";
import { GenAICleanupRequiredDialog } from "./GenAICleanupRequiredDialog";
import { useOpalBranding } from "../../../hooks/useOpalBranding/useOpalBranding";

type DisableGenAIConfirmDialogProps = {
    loading?: boolean;
    error?: string | undefined | null;
    onCancel: () => void;
    onConfirm: () => void;
    productGroupNames?: string[];
    otherGroupNames?: string[];
};

export const DisableGenAIConfirmDialog = ({
    loading,
    error,
    onCancel,
    onConfirm,
    productGroupNames = [],
    otherGroupNames = []
}: DisableGenAIConfirmDialogProps) => {
    const { opalName } = useOpalBranding();
    const showCleanupRequired = productGroupNames.length > 0 || otherGroupNames.length > 0;

    if (showCleanupRequired) {
        return (
            <GenAICleanupRequiredDialog
                onClose={onCancel}
                productGroupNames={productGroupNames}
                otherGroupNames={otherGroupNames}
            />
        );
    }

    return (
        <DialogNew
            data-cy="genai-confirm-disable-modal"
            footerButtonList={[
                <Button data-cy="genai-cancel-button" key={0} isDisabled={loading} style="plain" onClick={onCancel}>
                    Cancel
                </Button>,
                <Button
                    key={1}
                    data-cy="genai-confirm-button"
                    isDisabled={loading}
                    isLoading={loading}
                    loadingText={`Disable ${opalName}`}
                    style="danger"
                    onClick={onConfirm}
                >
                    Disable {opalName}
                </Button>
            ]}
            title={`Disable ${opalName}`}
            hasCloseButton
            hasOverlay
            onClose={onCancel}
        >
            <div className="push--bottom">
                {error && (
                    <Attention type="bad-news" className="push--bottom">
                        {error}
                    </Attention>
                )}
                <Typography type="body" tag="div">
                    Disabling {opalName} will disable your organization's use of {opalName}, including {opalName}{" "}
                    Suggestions.
                </Typography>
                <Typography type="body" className="push--top" tag="div">
                    While {opalName} is disabled, {opalName} permissions cannot be assigned to users or groups.
                </Typography>
                <Typography type="body" className="push--top" tag="div">
                    You can re-enable {opalName} later, but your current {opalName} permission settings will not be
                    restored automatically.
                </Typography>
            </div>
        </DialogNew>
    );
};

DisableGenAIConfirmDialog.displayName = "DisableGenAIConfirmDialog";
